import { useEffect, type RefObject } from 'react';

/**
 * Wires a closing element's completion to a finish signal, for anything that
 * plays an exit animation before it is dropped.
 *
 * Deterministic, no clocks: the browser announces every way a close can end.
 * `animationend` is the normal finish; `animationcancel` covers a slide-out
 * interrupted mid-flight; and when the panel has NO running animations at all
 * — reduced-motion's `animation: none`, or an environment without CSS
 * animations (jsdom) — `getAnimations()` says so and the close finishes
 * immediately, which is exactly what an instant-motion user asked for.
 *
 * Native listeners, not React's `onAnimationEnd` prop: they exist only while
 * a close is playing, and React's synthetic animation events never fire under
 * jsdom, which made the prop version untestable.
 *
 * Takes any Element: ui/OrcaLogo ends its hover run on it from an SVG group.
 */
export function useClosingAnimationEnd(
  ref: RefObject<Element | null>,
  closing: boolean,
  onClosingEnd?: () => void,
) {
  useEffect(() => {
    const el = ref.current;
    if (!closing || !onClosingEnd || !el) return;
    const finish = (event: Event) => {
      // The panel's own slide-out only — children animate too.
      if (event.target === el) onClosingEnd();
    };
    el.addEventListener('animationend', finish);
    el.addEventListener('animationcancel', finish);
    if (typeof el.getAnimations !== 'function' || el.getAnimations().length === 0) {
      onClosingEnd();
    }
    return () => {
      el.removeEventListener('animationend', finish);
      el.removeEventListener('animationcancel', finish);
    };
  }, [ref, closing, onClosingEnd]);
}
